/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Loads user-scoped context (companies, requirements) for ViewAppointments on mount.
 * @module logic/appointments/viewAppointmentsInitData
 */

import { logger } from "../../utils/logger.js";
import {
  getParticipatingCompanyByRFC,
  getParticipatingCompanies,
} from "../participatingCompany/index.js";
import { getUser } from "../user/index.js";

const companyNameCollator = new Intl.Collator("es", {
  sensitivity: "base",
  numeric: true,
});

/**
 * Maps participating companies to buyer-company select options.
 * @param {Array<Object>} participatingCompanies
 * @returns {Array<Object>}
 */
export const mapParticipatingCompaniesToBuyerOptions = (
  participatingCompanies,
  t,
) =>
  participatingCompanies
    .filter((company) => company.idEmpresaCompradora)
    .map((company) => ({
      _id: company.idEmpresaCompradora,
      razonSocial:
        company.razonSocial ||
        company.nombreComercial ||
        t("common.unnamedCompany"),
    }))
    .sort((left, right) =>
      companyNameCollator.compare(left.razonSocial || "", right.razonSocial || ""),
    );

/**
 * Fetches user and role-specific participating company data for the event.
 * @param {Object} options
 * @param {string} options.userId
 * @param {string} options.eventId
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {(key: string) => string} options.t
 * @returns {Promise<Object|null>} Context payload or null when user fetch fails
 */
export const loadViewAppointmentsUserContext = async ({
  userId,
  eventId,
  navigate,
  t,
}) => {
  logger.info("Fetching user data for userId:", { data: userId });
  const user = await getUser(userId, navigate);
  if (!user) {
    logger.error("Failed to fetch user data");
    return null;
  }

  const emptyAdminContext = {
    user,
    participatingCompanyData: null,
    participatingCompanies: [],
    buyerCompanies: [],
    buyerExecutives: [],
    requirements: [],
  };

  if (user.tipoDeUsuario === "11" || user.compradorActivo) {
    if (!user.RFC) {
      return { ...emptyAdminContext, requirements: [] };
    }

    const participatingCompany = await getParticipatingCompanyByRFC(
      eventId,
      user.RFC,
      navigate,
    );

    if (!participatingCompany) {
      return { ...emptyAdminContext, requirements: [] };
    }

    const requirements = participatingCompany.requerimientos
      ? participatingCompany.requerimientos.filter(
          (requirement) => requirement.idUsuarioComprador === userId,
        )
      : [];

    return {
      user,
      participatingCompanyData: participatingCompany,
      participatingCompanies: [],
      buyerCompanies: [],
      buyerExecutives: [],
      requirements,
    };
  }

  if (user.tipoDeUsuario === "5") {
    const participatingCompaniesData = await getParticipatingCompanies(
      eventId,
      navigate,
    );

    if (!participatingCompaniesData?.length) {
      return emptyAdminContext;
    }

    return {
      user,
      participatingCompanyData: null,
      participatingCompanies: participatingCompaniesData,
      buyerCompanies: mapParticipatingCompaniesToBuyerOptions(
        participatingCompaniesData,
        t,
      ),
      buyerExecutives: [],
      requirements: [],
    };
  }

  return emptyAdminContext;
};
